
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { RefreshCw, Download } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// Type definitions
interface PageViewCount {
  pathname: string;
  count: number;
}

interface DailyViewCount {
  date: string;
  views: number;
}

interface DeviceDistribution {
  device_type: string;
  count: number;
}

interface SiteStatisticsData {
  totalViews: number;
  uniqueVisitors: number;
  topPages: PageViewCount[];
  dailyViews: DailyViewCount[];
  deviceDistribution: DeviceDistribution[];
}

const SiteStatistics = () => {
  const [data, setData] = useState<SiteStatisticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d');
  
  // Function to fetch statistics data
  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      // Calculate date range based on selection
      const now = new Date();
      let startDate: Date;
      
      switch (timeRange) {
        case '30d':
          startDate = new Date(new Date().setDate(now.getDate() - 30));
          break;
        case '90d':
          startDate = new Date(new Date().setDate(now.getDate() - 90));
          break;
        case '7d':
        default:
          startDate = new Date(new Date().setDate(now.getDate() - 7));
          break;
      }
      
      const startDateStr = startDate.toISOString();
      
      // Get total page views
      const { count: totalViews, error: totalError } = await supabase
        .from('site_views')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDateStr);
        
      if (totalError) throw totalError;
      
      // Get unique visitors (by session_id)
      const { data: uniqueData, error: uniqueError } = await supabase
        .from('site_views')
        .select('session_id', { count: 'exact' }) // Keep count exact for potentially optimizing DB call
        .gte('created_at', startDateStr)
        .not('session_id', 'is', null); // Corrected: Check for session_id not being null
        
      if (uniqueError) throw uniqueError;
      
      const uniqueVisitors = new Set(uniqueData?.map(item => item.session_id)).size;
      
      // Get top pages and their creation dates
      const { data: pagesData, error: pagesError } = await supabase
        .from('site_views')
        .select('pathname, created_at') // Corrected: Include created_at
        .gte('created_at', startDateStr);
        
      if (pagesError) throw pagesError;
      
      // Count occurrences of each pathname
      const pageCounts: Record<string, number> = {};
      pagesData?.forEach(view => {
        const path = view.pathname || '/unknown';
        pageCounts[path] = (pageCounts[path] || 0) + 1;
      });
      
      // Convert to array and sort by count
      const topPages = Object.entries(pageCounts)
        .map(([pathname, count]) => ({ pathname, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10); // Top 10 pages
      
      // Daily views
      const dailyViewsMap: Record<string, number> = {};
      
      // Initialize all dates in the range with 0 views
      const dateIterator = new Date(startDate); // Re-initialize startDate for this loop
      const endDate = new Date(); // today
      
      // Ensure dateIterator starts from the actual startDate, not modified now
      let currentDateIter = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());


      while (currentDateIter <= endDate) {
        const dateStr = currentDateIter.toISOString().split('T')[0];
        dailyViewsMap[dateStr] = 0;
        currentDateIter.setDate(currentDateIter.getDate() + 1);
      }
      
      // Count views for each date
      pagesData?.forEach(view => {
        // Ensure created_at is available, which it should be now
        if (view.created_at) {
          const date = new Date(view.created_at).toISOString().split('T')[0];
          if (dailyViewsMap[date] !== undefined) {
            dailyViewsMap[date] += 1;
          }
        }
      });
      
      // Convert to array for chart
      const dailyViews = Object.entries(dailyViewsMap)
        .map(([date, views]) => ({ date, views }))
        .sort((a, b) => a.date.localeCompare(b.date));
      
      // Device distribution
      const { data: deviceData, error: deviceError } = await supabase
        .from('site_views')
        .select('device_type')
        .gte('created_at', startDateStr);
        
      if (deviceError) throw deviceError;
      
      // Count occurrences of each device type
      const deviceCounts: Record<string, number> = {};
      deviceData?.forEach(view => {
        const device = view.device_type || 'unknown';
        deviceCounts[device] = (deviceCounts[device] || 0) + 1;
      });
      
      // Convert to array for chart
      const deviceDistribution = Object.entries(deviceCounts)
        .map(([device_type, count]) => ({ device_type, count }));
      
      // Set the data
      setData({
        totalViews: totalViews || 0,
        uniqueVisitors,
        topPages,
        dailyViews,
        deviceDistribution,
      });
    } catch (err) {
      console.error('Error fetching site statistics:', err);
      setError('Failed to load statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load data on component mount and when timeRange changes
  useEffect(() => {
    fetchStatistics();
  }, [timeRange]);
  
  // Handle refresh button click
  const handleRefresh = () => {
    fetchStatistics();
  };
  
  // Colors for charts
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28'];
  
  // Export data as CSV
  const exportToCsv = () => {
    if (!data) return;
    
    // Prepare CSV content
    let csv = 'data:text/csv;charset=utf-8,';
    
    // Add daily views
    csv += 'Date,Views\n';
    data.dailyViews.forEach(({ date, views }) => {
      csv += `${date},${views}\n`;
    });

    // Add top pages
    csv += '\nPage,Views\n';
    data.topPages.forEach(({ pathname, count }) => {
      csv += `${pathname},${count}\n`;
    });

    // Add device distribution
    csv += '\nDevice Type,Count\n';
    data.deviceDistribution.forEach(({ device_type, count }) => {
      csv += `${device_type},${count}\n`;
    });
    
    // Encode and trigger download
    const encodedUri = encodeURI(csv);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `site-statistics-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Site Traffic Statistics</CardTitle>
          <CardDescription>Loading site traffic data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex justify-between">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-24" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
            <Skeleton className="h-64" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Site Traffic Statistics</CardTitle>
          <CardDescription className="text-red-500">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between md:items-center">
          <div>
            <CardTitle>Site Traffic Statistics</CardTitle>
            <CardDescription>
              Analyzing visitor activity across the platform
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2 mt-4 md:mt-0">
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCsv}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data && (
          <div className="space-y-6">
            {/* Time range selector */}
            <div className="flex justify-between items-center">
              <Tabs
                value={timeRange}
                onValueChange={(value) => setTimeRange(value as '7d' | '30d' | '90d')}
              >
                <TabsList>
                  <TabsTrigger value="7d">7 Days</TabsTrigger>
                  <TabsTrigger value="30d">30 Days</TabsTrigger>
                  <TabsTrigger value="90d">90 Days</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-2xl">{data.totalViews}</CardTitle>
                  <CardDescription>Total Page Views</CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-2xl">{data.uniqueVisitors}</CardTitle>
                  <CardDescription>Unique Visitors (Sessions)</CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-2xl">
                    {data.uniqueVisitors > 0 // Ensure uniqueVisitors is not zero
                      ? (data.totalViews / data.uniqueVisitors).toFixed(2) 
                      : '0'}
                  </CardTitle>
                  <CardDescription>Pages per Visitor</CardDescription>
                </CardHeader>
              </Card>
            </div>
            
            {/* Charts */}
            <Tabs defaultValue="traffic">
              <TabsList>
                <TabsTrigger value="traffic">Traffic Over Time</TabsTrigger>
                <TabsTrigger value="pages">Top Pages</TabsTrigger>
                <TabsTrigger value="devices">Device Types</TabsTrigger>
              </TabsList>
              <TabsContent value="traffic" className="pt-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.dailyViews}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          // Format date to be more readable
                          const date = new Date(value + "T00:00:00"); // Ensure correct date parsing
                          return `${date.getMonth() + 1}/${date.getDate()}`;
                        }}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        labelFormatter={(label) => {
                          // Format the date in the tooltip
                          const date = new Date(label + "T00:00:00"); // Ensure correct date parsing
                          return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="views" 
                        name="Page Views"
                        stroke="#8884d8" 
                        activeDot={{ r: 8 }} 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
              <TabsContent value="pages" className="pt-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.topPages.slice(0, 7)} margin={{ top: 5, right: 20, left: 10, bottom: 50 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="pathname" 
                        tick={{ fontSize: 12 }}
                        angle={-35}
                        textAnchor="end"
                        interval={0}
                        height={60}
                        tickFormatter={(value) => {
                          // Truncate long pathnames
                          return value.length > 15 ? value.substr(0, 12) + '...' : value;
                        }}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip 
                        labelFormatter={(label) => `Page: ${label}`}
                      />
                      <Bar dataKey="count" name="Views" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">All Pages ({timeRange})</h4>
                  <div className="max-h-64 overflow-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Page Path</TableHead>
                          <TableHead className="text-right">Views</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.topPages.length > 0 ? (
                          data.topPages.map((page, index) => (
                            <TableRow key={index}>
                              <TableCell className="truncate max-w-xs" title={page.pathname}>{page.pathname}</TableCell>
                              <TableCell className="text-right">{page.count}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center text-muted-foreground">
                              No page view data for this period.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="devices" className="pt-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.deviceDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={110}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="device_type"
                        label={({ device_type, percent, count }) => 
                          `${device_type}: ${count} (${(percent * 100).toFixed(0)}%)`
                        }
                      >
                        {data.deviceDistribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Legend />
                      <Tooltip formatter={(value, name) => [`${value} views`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SiteStatistics;

